import React, { useState, useRef, Suspense, useEffect } from "react";
import { Link } from "react-router-dom";
import { Canvas, useFrame } from "@react-three/fiber";
import { 
  OrbitControls, 
  Text, 
  Float, 
  Html, 
  PerspectiveCamera,
  Environment
} from "@react-three/drei";
import * as THREE from "three";
import AOS from "aos";
import "aos/dist/aos.css";
import "../Styles/Contact.css";
import {
  AiOutlineMail,
  AiOutlinePhone,
  AiOutlineEnvironment,
  AiOutlineClockCircle,
  AiOutlineSend,
  AiOutlineCheckCircle,
  AiOutlineLoading3Quarters,
  AiOutlineGlobal,
  AiOutlineMessage,
  AiOutlineUser,
  AiOutlineSchedule,
} from "react-icons/ai";
import {
  FaFacebook,
  FaWhatsapp,
  FaLinkedin,
  FaInstagram,
  FaGithub,
  FaPaperPlane,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { MdOutlineSupportAgent } from "react-icons/md";

// 3D Communication Network Component
function CommunicationNetwork() {
  const networkRef = useRef();
  const pointsRef = useRef();
  const [hovered, setHovered] = useState(null);

  useFrame((state) => {
    if (networkRef.current) {
      networkRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.2) * 0.1;
    }
  });

  // Create connection points
  const points = [
    { position: [2, 1, 0], color: "#6366f1", label: "Kenya" },
    { position: [-2, 0.5, 1], color: "#10b981", label: "USA" },
    { position: [0, -1, 2], color: "#8b5cf6", label: "UK" },
    { position: [1.5, -0.5, -1.5], color: "#f59e0b", label: "Germany" },
    { position: [-1.5, 1.2, -0.5], color: "#ec4899", label: "Japan" },
    { position: [0.5, 1.5, 1.8], color: "#06b6d4", label: "Australia" },
  ];

  // Create connections between points
  const connections = [
    [0, 1], [0, 2], [0, 3], [1, 4], [2, 5], [3, 4], [4, 5]
  ];

  return (
    <group ref={networkRef}>
      <ambientLight intensity={0.5} />
      <pointLight position={[10, 10, 10]} intensity={1} color="#6366f1" />
      <pointLight position={[-10, -10, -10]} intensity={0.5} color="#8b5cf6" />
      
      {/* Communication lines */}
      {connections.map(([start, end], idx) => (
        <line key={idx}>
          <bufferGeometry attach="geometry">
            <float32BufferAttribute
              attach="attributes-position"
              args={[
                new Float32Array([
                  ...points[start].position,
                  ...points[end].position
                ]), 
                3
              ]}
            />
          </bufferGeometry>
          <lineBasicMaterial
            color="#6366f1"
            transparent
            opacity={0.4}
            linewidth={2}
          />
        </line>
      ))}

      {/* Nodes */}
      {points.map((point, idx) => (
        <Float
          key={idx}
          speed={2}
          rotationIntensity={0.5}
          floatIntensity={2}
        >
          <mesh
            position={point.position}
            onPointerOver={() => setHovered(idx)}
            onPointerOut={() => setHovered(null)}
          >
            <sphereGeometry args={[0.1, 16, 16]} />
            <meshStandardMaterial
              color={point.color}
              emissive={point.color}
              emissiveIntensity={hovered === idx ? 1 : 0.5}
              transparent
              opacity={0.9}
              metalness={0.8}
              roughness={0.2}
            />
            
            {/* Glow effect */}
            {hovered === idx && (
              <mesh position={[0, 0, 0]}>
                <sphereGeometry args={[0.15, 16, 16]} />
                <meshBasicMaterial
                  color={point.color}
                  transparent
                  opacity={0.3}
                />
              </mesh>
            )}
          </mesh>

          {/* Label */}
          {hovered === idx && (
            <Html position={point.position} center>
              <div className="network-label">
                {point.label}
              </div>
            </Html>
          )}
        </Float>
      ))}
      
      {/* Central hub */}
      <mesh position={[0, 0, 0]}>
        <dodecahedronGeometry args={[0.3, 0]} />
        <meshStandardMaterial
          color="#6366f1"
          emissive="#6366f1"
          emissiveIntensity={0.8}
          metalness={0.9}
          roughness={0.1}
        />
      </mesh>
    </group>
  );
}

// Floating Message Particles
function MessageParticles() {
  const particlesRef = useRef();
  const particles = useRef([]);

  useFrame((state) => {
    if (particlesRef.current) {
      particles.current.forEach((particle, i) => {
        particle.rotation.x = Math.sin(state.clock.elapsedTime + i) * 0.1;
        particle.rotation.y = Math.cos(state.clock.elapsedTime + i) * 0.1;
      });
    }
  });

  const particlePositions = [
    [-1, 0.5, 0], [1, -0.5, 1], [0.5, 1, -1], [-0.5, -1, 0.5],
    [1.5, 0, -0.5], [-1.5, 0.5, 1], [0, -1.5, -1]
  ];

  return (
    <group ref={particlesRef}>
      {particlePositions.map((pos, idx) => (
        <Float
          key={idx}
          speed={1 + Math.random()}
          rotationIntensity={0.5}
          floatIntensity={1 + Math.random()}
        >
          <mesh
            position={pos}
            ref={el => particles.current[idx] = el}
          >
            <icosahedronGeometry args={[0.05, 0]} />
            <meshStandardMaterial
              color="#10b981"
              emissive="#10b981"
              emissiveIntensity={0.5}
              transparent
              opacity={0.7}
            />
          </mesh>
        </Float>
      ))}
    </group>
  );
}

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    subject: "",
    message: "",
    service: "",
    urgency: "normal"
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [activeTimeZone, setActiveTimeZone] = useState("Africa/Nairobi");

  useEffect(() => {
    AOS.init({
      duration: 1500,
      once: true,
    });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    // Reset form after 5 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: "",
        email: "",
        company: "",
        phone: "",
        subject: "",
        message: "",
        service: "",
        urgency: "normal"
      });
    }, 5000);
  };

  // Contact methods
  const contactMethods = [
    {
      icon: <AiOutlineMail />,
      title: "Email Us",
      details: "sales@fusion-xe.com",
      action: "mailto:sales@fusion-xe.com",
      color: "bg-blue-500/10",
      delay: "100"
    },
    {
      icon: <AiOutlinePhone />,
      title: "Call Us",
      details: "+254 741 210060",
      action: "tel:+254741210060",
      color: "bg-green-500/10",
      delay: "200"
    },
    {
      icon: <AiOutlineEnvironment />,
      title: "Visit Us",
      details: "Nairobi, Kenya",
      action: "https://maps.google.com",
      color: "bg-purple-500/10",
      delay: "300"
    },
    {
      icon: <AiOutlineClockCircle />,
      title: "Office Hours",
      details: "24/7 Global Support",
      action: "#",
      color: "bg-amber-500/10",
      delay: "400"
    },
  ];

  // Services
  const services = [
    "AI & Machine Learning",
    "Cloud Solutions",
    "Web Development",
    "Mobile Apps",
    "Cybersecurity",
    "Data Analytics",
    "IoT Solutions",
    "Blockchain"
  ];

  // Time zones
  const timeZones = [
    { zone: "Africa/Nairobi", label: "Nairobi (EAT)", offset: "+3", current: true },
    { zone: "America/New_York", label: "New York (EST)", offset: "-5" },
    { zone: "Europe/London", label: "London (GMT)", offset: "0" },
    { zone: "Asia/Tokyo", label: "Tokyo (JST)", offset: "+9" },
    { zone: "Australia/Sydney", label: "Sydney (AEST)", offset: "+10" },
  ];

  // Global offices
  const globalOffices = [
    {
      city: "Nairobi",
      country: "Kenya",
      role: "Headquarters",
      address: "Westlands, Nairobi, Kenya",
      email: "ke@fusion-xe.com",
      phone: "+254 741 210060"
    },
    {
      city: "New York",
      country: "USA",
      role: "North America Hub",
      address: "Manhattan, NY, USA",
      email: "us@fusion-xe.com",
      phone: "+1 (555) 123-4567"
    },
    {
      city: "London",
      country: "UK",
      role: "European Operations",
      address: "London, UK",
      email: "uk@fusion-xe.com",
      phone: "+44 20 7123 4567"
    },
    {
      city: "Sydney",
      country: "Australia",
      role: "APAC Regional Office",
      address: "Sydney, NSW, Australia",
      email: "au@fusion-xe.com",
      phone: "+61 2 1234 5678"
    },
  ];

  return (
    <div className="contact-container">
      {/* Hero Section with 3D Network */}
      <section className="contact-hero">
        <div className="contact-hero-background">
          <div className="contact-glow"></div>
          <div className="contact-grid"></div>
          <div className="contact-particles">
            {[...Array(15)].map((_, i) => (
              <div key={i} className="contact-particle" style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                width: `${Math.random() * 3 + 1}px`,
                height: `${Math.random() * 3 + 1}px`,
              }}></div>
            ))}
          </div>
        </div>

        <div className="contact-hero-content">
          <div data-aos="fade-up" className="hero-text">
            <div className="contact-badge">
              <AiOutlineMessage />
              <span>Connect With Us</span>
            </div>
            
            <h1 className="contact-title">
              <span className="gradient-text">Let's Build</span> The Future
              <span className="contact-subtitle">Together</span>
            </h1>
            
            <p className="contact-description">
              Ready to transform your business with cutting-edge technology? 
              Our global team is here to listen, understand, and deliver solutions 
              that propel you ahead of the competition.
            </p>
          </div>

          {/* 3D Network Visualization */}
          <div data-aos="fade-up" data-aos-delay="300" className="network-visualization">
            <Canvas camera={{ position: [0, 0, 5], fov: 50 }}>
              <Suspense fallback={null}>
                <CommunicationNetwork />
                <MessageParticles />
                <Environment preset="city" />
                <OrbitControls
                  enableZoom={true}
                  enablePan={false}
                  enableRotate={true}
                  autoRotate={true}
                  autoRotateSpeed={0.5}
                  minDistance={3}
                  maxDistance={8}
                />
              </Suspense>
            </Canvas>
            
            <div className="network-info">
              <div className="network-info-item">
                <div className="network-dot" style={{ background: "#6366f1" }}></div>
                <span>Global Network Nodes</span>
              </div>
              <div className="network-info-item">
                <div className="network-dot" style={{ background: "#10b981" }}></div>
                <span>Active Communications</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="contact-methods-section">
        <div className="container">
          <div data-aos="fade-up" className="section-header">
            <div className="section-badge">
              <MdOutlineSupportAgent />
              <span>Multiple Ways to Connect</span>
            </div>
            <h2>
              Choose Your <span className="gradient-text">Preferred</span> Channel
            </h2>
            <p>Reach out through any of our global communication channels</p>
          </div>

          <div className="contact-methods-grid">
            {contactMethods.map((method, index) => (
              <a
                key={index}
                href={method.action}
                data-aos="fade-up"
                data-aos-delay={method.delay}
                className="contact-method-card"
              >
                <div className={`contact-method-icon ${method.color}`}>
                  {method.icon}
                </div>
                <div className="contact-method-content">
                  <h3>{method.title}</h3>
                  <p>{method.details}</p>
                  <div className="contact-method-hover">
                    <FaPaperPlane />
                    <span>Get in Touch</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="contact-form-section">
        <div className="container">
          <div className="contact-form-wrapper">
            <div data-aos="fade-right" className="form-content">
              <div className="form-header">
                <h2>
                  Send Us a <span className="gradient-text">Message</span>
                </h2>
                <p>
                  Fill out the form below and our team will get back to you within 
                  <span className="highlight"> 24 hours</span>.
                </p>
              </div>

              {isSubmitted ? (
                <div className="success-message">
                  <AiOutlineCheckCircle className="success-icon" />
                  <h3>Message Sent Successfully!</h3>
                  <p>
                    Thank you for reaching out. Our team will contact you shortly. 
                    In the meantime, feel free to explore our services.
                  </p>
                  <Link to="/services" className="btn-secondary">
                    Explore Services
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="name">
                        <AiOutlineUser />
                        <span>Full Name *</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">
                        <AiOutlineMail />
                        <span>Email Address *</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="your.email@company.com"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="company">
                        <AiOutlineGlobal />
                        <span>Company</span>
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Your company name"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">
                        <AiOutlinePhone />
                        <span>Phone Number</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+254 741 210060"
                      />
                    </div>

                    <div className="form-group full-width">
                      <label htmlFor="subject">
                        <AiOutlineMessage />
                        <span>Subject *</span>
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        placeholder="What would you like to discuss?"
                      />
                    </div>

                    <div className="form-group full-width">
                      <label htmlFor="service">
                        <AiOutlineCheckCircle />
                        <span>Service Interested In</span>
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                      >
                        <option value="">Select a service</option>
                        {services.map((service, idx) => (
                          <option key={idx} value={service}>
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group full-width">
                      <label htmlFor="urgency">
                        <AiOutlineSchedule />
                        <span>Urgency Level</span>
                      </label>
                      <div className="urgency-options">
                        {["low", "normal", "high", "urgent"].map((level) => (
                          <label key={level} className="urgency-option">
                            <input
                              type="radio"
                              name="urgency"
                              value={level}
                              checked={formData.urgency === level}
                              onChange={handleInputChange}
                            />
                            <span className={`urgency-dot ${level}`}></span>
                            <span className="urgency-label">{level.charAt(0).toUpperCase() + level.slice(1)}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="form-group full-width">
                      <label htmlFor="message">
                        <AiOutlineMessage />
                        <span>Your Message *</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows="6"
                        placeholder="Describe your project or inquiry in detail..."
                      />
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`btn-primary btn-glow ${isSubmitting ? 'submitting' : ''}`}
                    >
                      {isSubmitting ? (
                        <>
                          <AiOutlineLoading3Quarters className="loading-icon" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <AiOutlineSend />
                          <span>Send Message</span>
                        </>
                      )}
                    </button>
                    
                    <div className="form-note">
                      <AiOutlineCheckCircle />
                      <span>All messages are encrypted and secure</span>
                    </div>
                  </div>
                </form>
              )}
            </div>

            <div data-aos="fade-left" className="contact-info-sidebar">
              <div className="timezone-selector">
                <h3>
                  <AiOutlineClockCircle />
                  <span>Global Time Zones</span>
                </h3>
                <div className="timezone-list">
                  {timeZones.map((tz, idx) => (
                    <button
                      key={idx}
                      className={`timezone-item ${tz.current ? 'active' : ''}`}
                      onClick={() => setActiveTimeZone(tz.zone)}
                    >
                      <span className="timezone-city">{tz.label}</span>
                      <span className="timezone-offset">{tz.offset}</span>
                      {tz.current && (
                        <span className="current-badge">Current</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="global-offices">
                <h3>
                  <FaMapMarkerAlt />
                  <span>Global Offices</span>
                </h3>
                <div className="office-list">
                  {globalOffices.map((office, idx) => (
                    <div key={idx} className="office-card">
                      <div className="office-header">
                        <h4>{office.city}</h4>
                        <span className="office-role">{office.role}</span>
                      </div>
                      <p className="office-country">{office.country}</p>
                      <p className="office-address">{office.address}</p>
                      <div className="office-contact">
                        <a href={`mailto:${office.email}`}>{office.email}</a>
                        <a href={`tel:${office.phone}`}>{office.phone}</a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="contact-stats">
                <div className="stat-item">
                  <div className="stat-value">24/7</div>
                  <div className="stat-label">Support Available</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">2h</div>
                  <div className="stat-label">Average Response Time</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">98%</div>
                  <div className="stat-label">Satisfaction Rate</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Links */}
      <div className="contact-social-links">
        {[
          { icon: <FaWhatsapp />, href: "https://wa.me/254741210060", label: "WhatsApp" },
          { icon: <FaLinkedin />, href: "https://www.linkedin.com/in/collin-mwenda-software-engineer", label: "LinkedIn" },
          { icon: <FaXTwitter />, href: "https://twitter.com/fusionxe", label: "Twitter" },
          { icon: <FaFacebook />, href: "https://www.facebook.com/kingcolloh0", label: "Facebook" },
          { icon: <FaGithub />, href: "https://github.com/fusionxe", label: "GitHub" },
          { icon: <FaInstagram />, href: "https://instagram.com/fusionxe", label: "Instagram" },
        ].map((social, index) => (
          <a
            key={index}
            href={social.href}
            target="_blank"
            rel="noopener noreferrer"
            className="contact-social-link"
            data-aos="fade-up"
            data-aos-delay={index * 100}
            aria-label={social.label}
          >
            {social.icon}
            <span className="social-tooltip">{social.label}</span>
          </a>
        ))}
      </div>

      {/* Live Chat Widget */}
      <div className="live-chat-widget">
        <button className="chat-toggle">
          <AiOutlineMessage />
          <span className="chat-badge">Live</span>
        </button>
        <div className="chat-indicator">
          <div className="chat-pulse"></div>
          <span>Chat with AI Assistant</span>
        </div>
      </div>
    </div>
  );
}

export default Contact;